import {
  createFileRoute,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-keys";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { CardTitle, ErrorState, FieldGroup, Text } from "@/components/ui/text";
import { FormGrid } from "@/components/ui/form-grid";
import { PageNavigationHeader } from "@/components/ui/page-navigation-header";
import { Skeleton } from "@/components/ui/skeleton";
import { VStack } from "@/components/ui/v-stack";
import { getAdminEntity } from "@/lib/api/admin/entities";
import {
  formatAdminEntityTypeLabel,
  formatAdminDate,
} from "@/components/admin/formatting";
import { fromIntlLanguage } from "@/lib/i18n/config";
import { TicketActivitySection } from "@/components/shared/ticket-activity-section";

function AdminEntityDetailPage() {
  const router = useRouter();
  const navigate = useNavigate();
  const language = fromIntlLanguage(useTranslation().i18n.language);
  const { t: tAdminEntities } = useTranslation("admin.entities");
  const { t: tCommon } = useTranslation("common");
  const params = Route.useParams();
  const id = params?.id;

  const {
    data: entity = null,
    isLoading: loading,
    isError: errorKind,
    refetch: refetchEntity,
  } = useQuery({
    queryKey: queryKeys.admin.entity(id ?? ""),
    queryFn: () => getAdminEntity(id!),
    enabled: !!id,
  });

  return (
    <VStack gap="xl" data-testid="admin-entity-detail-page">
      <PageNavigationHeader
        contextLabel={tAdminEntities("title")}
        title={
          loading ? (
            <Skeleton className="h-7 w-64" />
          ) : (
            (entity?.name ?? tAdminEntities("title"))
          )
        }
        metadata={
          entity
            ? `${tAdminEntities("columns.created")} ${formatAdminDate(entity.created_at, language)}`
            : tCommon("loading")
        }
        status={
          entity ? (
            <Text
              size="sm"
              weight="medium"
              className={
                entity.is_approved
                  ? "text-[color:var(--success)]"
                  : "text-[color:var(--warning)]"
              }
            >
              {entity.is_approved
                ? tAdminEntities("approved")
                : tAdminEntities("pending")}
            </Text>
          ) : (
            <Skeleton className="h-7 w-24" rounded="full" />
          )
        }
        onBack={() => {
          if (typeof window !== "undefined" && window.history.length > 1) {
            router.history.back();
            return;
          }
          void navigate({ to: "/admin/entities" });
        }}
        backAriaLabel={tCommon("back")}
        contextAriaLabel={tAdminEntities("backToEntities")}
      />

      {entity?.tickets ? (
        <TicketActivitySection tickets={entity.tickets} />
      ) : null}

      {errorKind ? (
        <ErrorState
          title={tCommon("error")}
          description={tAdminEntities("failedToLoad")}
          action={
            <Button
              color="neutral"
              variant="outline"
              onClick={() => void refetchEntity()}
            >
              {tCommon("retry")}
            </Button>
          }
          className="h-96"
        />
      ) : null}

      <FormGrid cols="lg" gap="xl">
        <Card variant="bordered" padding="none">
          <CardHeader>
            <CardTitle>{tAdminEntities("companyInfo")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {loading || !entity ? (
              <VStack>
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-2/3" />
              </VStack>
            ) : (
              <FormGrid cols="sm" gap="xl">
                <FieldGroup
                  label={tAdminEntities("fields.name")}
                  value={entity.name}
                  valueClassName="text-sm"
                />
                <FieldGroup
                  label={tAdminEntities("fields.type")}
                  value={formatAdminEntityTypeLabel(
                    entity.type,
                    tAdminEntities,
                  )}
                  valueClassName="text-sm"
                />
                <FieldGroup
                  label={tAdminEntities("fields.country")}
                  value={entity.country?.name ?? "—"}
                  valueClassName="text-sm"
                />
                <FieldGroup
                  label={tAdminEntities("fields.city")}
                  value={entity.city?.name ?? "—"}
                  valueClassName="text-sm"
                />
                <FieldGroup
                  label={tAdminEntities("fields.address")}
                  value={entity.address ?? "—"}
                  valueClassName="text-sm"
                  className="sm:col-span-2"
                />
                <FieldGroup
                  label={tAdminEntities("fields.legalEntity")}
                  value={
                    entity.is_legal_entity ? tCommon("yes") : tCommon("no")
                  }
                  valueClassName="text-sm"
                />
                <FieldGroup
                  label={tAdminEntities("fields.timezone")}
                  value={entity.time_zone ?? "—"}
                  valueClassName="text-sm"
                />
              </FormGrid>
            )}
          </CardContent>
        </Card>

        <Card variant="bordered" padding="none">
          <CardHeader>
            <CardTitle>{tAdminEntities("contactInfo")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {loading || !entity ? (
              <VStack>
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
              </VStack>
            ) : (
              <FormGrid cols="sm" gap="xl">
                <FieldGroup
                  label={tAdminEntities("fields.email")}
                  value={entity.email ?? "—"}
                  valueClassName="text-sm"
                />
                <FieldGroup
                  label={tAdminEntities("fields.phone")}
                  value={entity.phone_number ?? "—"}
                  valueClassName="text-sm"
                />
                <FieldGroup
                  label={tAdminEntities("fields.owner")}
                  value={entity.owner?.name ?? "—"}
                  valueClassName="text-sm"
                />
                <FieldGroup
                  label={tAdminEntities("fields.ownerEmail")}
                  value={entity.owner?.email ?? "—"}
                  valueClassName="text-sm"
                />
              </FormGrid>
            )}
          </CardContent>
        </Card>
      </FormGrid>
    </VStack>
  );
}

export const Route = createFileRoute("/_app/admin/entities/$id")({
  component: AdminEntityDetailPage,
});
